import { request } from '@/lib/api'

/**
 * Входящая почта (тикет #121): IMAP-ящики в администрировании и очередь разбора писем,
 * которые не удалось сопоставить с карточкой.
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт пустые поля в JSON вовсе.
 */

export type MailboxSecurity = 'ssl' | 'starttls' | 'none'
export type MailboxKind = 'shared' | 'personal'
/** `rejected` — сервер отказал (пароль, папка): ящик не опрашивается, пока его не сохранят. */
export type MailboxPollStatus = 'idle' | 'ok' | 'failed' | 'rejected'

export interface Mailbox {
  id: string
  name: string
  kind: MailboxKind
  ownerUserId?: string | null
  ownerName?: string | null
  host?: string | null
  port?: number | null
  security: MailboxSecurity
  username?: string | null
  passwordSet: boolean
  folder: string
  enabled: boolean
  pollStatus: MailboxPollStatus
  lastPolledAt?: string | null
  lastError?: string | null
  retryAfter?: string | null
}

export interface MailboxUpdate {
  name: string
  kind: MailboxKind
  ownerUserId: string | null
  host: string | null
  port: number | null
  security: MailboxSecurity
  username: string | null
  /** `undefined` — оставить сохранённый, пустая строка — стереть. */
  password?: string
  folder: string | null
  enabled: boolean
}

export interface PollResult {
  received: number
  duplicates: number
  unmatched: number
  /** ok, failed, rejected, busy (опрос уже идёт), disabled. */
  status: string
  error?: string | null
}

export interface InboundFile {
  id: string
  fileName: string
  /** Вердикт антивируса: pending, clean, infected, failed. */
  scanStatus: string
  sizeBytes: number
}

export interface QueueItem {
  id: string
  mailboxId: string
  mailboxName: string
  fromAddress?: string | null
  fromName?: string | null
  subject?: string | null
  excerpt?: string | null
  sentAt?: string | null
  receivedAt: string
  reason?: string | null
  /** Подсказка по домену почты — только подсказка, сама она письмо никуда не кладёт. */
  suggestedCounterpartyId?: string | null
  suggestedCounterpartyName?: string | null
  attachments?: InboundFile[]
  skippedAttachments?: string | null
}

export type AssignTarget = 'counterparty' | 'contact' | 'deal'

export function fetchMailboxes(signal?: AbortSignal): Promise<Mailbox[]> {
  return request<{ mailboxes?: Mailbox[] }>('/admin/mailboxes', { signal }).then((body) => body.mailboxes ?? [])
}

export function createMailbox(body: MailboxUpdate): Promise<Mailbox> {
  return request<Mailbox>('/admin/mailboxes', { method: 'POST', body })
}

export function updateMailbox(id: string, body: MailboxUpdate): Promise<Mailbox> {
  return request<Mailbox>(`/admin/mailboxes/${id}`, { method: 'PUT', body })
}

export function deleteMailbox(id: string): Promise<void> {
  return request<void>(`/admin/mailboxes/${id}`, { method: 'DELETE' })
}

export function pollMailbox(id: string): Promise<PollResult> {
  return request<PollResult>(`/admin/mailboxes/${id}/poll`, { method: 'POST', body: {} })
}

export function fetchInboxQueue(signal?: AbortSignal): Promise<QueueItem[]> {
  return request<{ items?: QueueItem[] }>('/mail/inbox', { signal }).then((body) => body.items ?? [])
}

export function assignInbound(id: string, entityType: AssignTarget, entityId: string): Promise<void> {
  return request<void>(`/mail/inbox/${id}/assign`, { method: 'POST', body: { entityType, entityId } })
}

/** Обращение из письма (#123): в ту же очередь, что обращения из кабинета. */
export function ticketFromInbound(id: string, counterpartyId: string): Promise<{ id: string; number: string }> {
  return request<{ id: string; number: string }>(`/mail/inbox/${id}/ticket`, {
    method: 'POST',
    body: { counterpartyId },
  })
}

export function dismissInbound(id: string): Promise<void> {
  return request<void>(`/mail/inbox/${id}/dismiss`, { method: 'POST', body: {} })
}
